import React, { useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ApiService, CompanyDocuments, RetrieveDocumentGenerationJob } from 'api';
import { useStore } from 'common/store';
import { MessageBox } from 'components';
import { getDocumentGenerationJobsDocumentsUrl } from 'pages/DocumentGenerationJobs/DocumentGenerationJobs.utils';
import DocumentGenerationJobsTableItem from './DocumentGenerationJobTableItem/DocumentGenerationJobTableItem';
import {
  EMPTY_STATE_HEADER,
  EMPTY_STATE_SUBHEADER,
  ERROR_STATE_HEADER,
  QUERY_CONSTANTS,
  TABLE_CONSTANTS,
} from '../../constants';
import DocumentGenerationJobCreateButton from '../DocumentGenerationJobCreateButton/DocumentGenerationJobCreateButton';

interface DocumentGenerationJobsTableProps {
  companyDocuments: CompanyDocuments[] | undefined;
}

const DocumentGenerationJobsTable = ({ companyDocuments }: DocumentGenerationJobsTableProps) => {
  const tableHeaders = useMemo(() => TABLE_CONSTANTS.HEADERS, []);
  const { apiCompanyDocumentGenerationJobsList } = ApiService;
  const [{ companyInfo, firmInfo }] = useStore() as any;
  const { firmSlugParam, companySlugParam } = useParams<{ firmSlugParam: string; companySlugParam: string }>();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const documentsPageUrl = useMemo(
    () =>
      getDocumentGenerationJobsDocumentsUrl({
        companySlug: companyInfo?.slug,
        firmSlug: firmInfo?.slug,
        routeCompanySlug: companySlugParam,
        routeFirmSlug: firmSlugParam,
      }),
    [companyInfo?.slug, companySlugParam, firmInfo?.slug, firmSlugParam]
  );

  const FINAL_STATUSES = [RetrieveDocumentGenerationJob.status.COMPLETED, RetrieveDocumentGenerationJob.status.FAILED];

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [QUERY_CONSTANTS.getDocumentGenerationJobs, companyInfo.id, page, rowsPerPage],
    queryFn: async (): Promise<RetrieveDocumentGenerationJob[]> => {
      const response = await apiCompanyDocumentGenerationJobsList(companyInfo.id, page, rowsPerPage);
      // overriding type as the api service returns back a different type than what is actually returned
      return response as unknown as RetrieveDocumentGenerationJob[];
    },
    refetchInterval: (query) => {
      const jobs = query.state.data;
      if (!jobs?.length) return false;
      const hasPending = jobs.some(job => job.status !== undefined && !FINAL_STATUSES.includes(job.status));
      return hasPending ? 15000 : false;
    },
  });

  if (!companyInfo?.id) return <></>;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Typography>{ERROR_STATE_HEADER}</Typography>;
  }

  if (companyDocuments !== undefined && companyDocuments.length === 0) {
    return (
      <MessageBox
        title="No documents to process."
        tagline="Upload documents first to start a Document Processing Job."
        fullWidth={false}
        action={documentsPageUrl ? (
          <Button
            component={Link}
            to={documentsPageUrl}
            variant="outlined"
            color="primary">
            Go to Documents
          </Button>
        ) : undefined}
      />
    );
  }

  // Only the first page can mean "no jobs at all". A later page can come back
  // empty (the backend returns an empty page past the end of the set), and
  // swapping the table for the empty state there would strand the user with
  // no pagination controls to navigate back.
  if (page === 0 && !data?.length) {
    return (
      <MessageBox
        title={EMPTY_STATE_HEADER}
        tagline={EMPTY_STATE_SUBHEADER}
        fullWidth={false}
        action={<DocumentGenerationJobCreateButton />}
      />
    );
  }

  return (
    <>
      <Table>
        <TableHead>
          <TableRow>
            {tableHeaders.map(header => (
              <TableCell key={header.key}>
                <Typography variant="body1">{header.label}</Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data?.map(generationJob => (
            <DocumentGenerationJobsTableItem key={generationJob.job_id} generationJob={generationJob} />
          ))}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 10, 15, 25, 50]}
        component="div"
        // -1 indicates unknown total count
        count={-1}
        labelDisplayedRows={() => ''}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        nextIconButtonProps={{ disabled: isFetching || (data?.length ?? 0) < rowsPerPage }}
      />
    </>
  );
};

export default DocumentGenerationJobsTable;
